import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
    Plus,
    Search,
    Edit2,
    Trash2,
    MapPin,
    Phone,
    Mail,
    Clock,
    Navigation,
    Building2,
    Star,
    Save,
    X,
    ChevronLeft,
    AlertCircle,
    CheckCircle,
    Loader2,
    Map,
    ExternalLink,
    RefreshCw
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { branchesAPI } from '../../services/api';
import { provinces, branchServices } from '../../data/mockData';

const BranchesManagementPage = () => {
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingBranch, setEditingBranch] = useState(null);
    const [saving, setSaving] = useState(false);
    const [notification, setNotification] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        address: '',
        district: '',
        province: '',
        phone: [''],
        fax: '',
        email: '',
        hours: 'Mon - Fri : 8:30 AM - 5:00 PM | Sat : 8:30 AM - 1:00 PM',
        services: [],
        coordinates: { lat: '', lng: '' },
        isHeadOffice: false
    });

    // Available services
    const availableServices = branchServices.map(s => s.label);

    // Subscribe to real-time branch updates
    useEffect(() => {
        let unsubscribe;

        const loadBranches = async () => {
            setLoading(true);
            try {
                // Subscribe to Firebase branches in real-time
                unsubscribe = branchesAPI.subscribeToBranches(
                    (firebaseBranches) => {
                        // Always use Firebase branches (even if empty)
                        // This ensures real-time sync works correctly
                        setBranches(firebaseBranches || []);
                        setLoading(false);
                        console.log('📡 Branches updated:', firebaseBranches?.length || 0);
                    },
                    (error) => {
                        console.error('Error loading branches:', error);
                        // No mock data fallback - show error with clear messaging
                        setBranches([]);
                        setLoading(false);
                        showNotification('Failed to load branches from Firebase. Please check your connection and permissions.', 'error');
                    }
                );
            } catch (error) {
                console.error('Error:', error);
                setBranches([]);
                setLoading(false);
                showNotification('Failed to connect to Firebase', 'error');
            }
        };

        loadBranches();

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, []);

    // Filter branches
    const filteredBranches = branches.filter(branch =>
        branch.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        branch.district?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        branch.province?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Show notification
    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 4000);
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            name: '',
            code: '',
            address: '',
            district: '',
            province: '',
            phone: [''],
            fax: '',
            email: '',
            hours: 'Mon - Fri : 8:30 AM - 5:00 PM | Sat : 8:30 AM - 1:00 PM',
            services: [],
            coordinates: { lat: '', lng: '' },
            isHeadOffice: false
        });
        setEditingBranch(null);
    };

    // Open add modal
    const handleAdd = () => {
        resetForm();
        setShowModal(true);
    };

    // Open edit modal
    const handleEdit = (branch) => {
        setEditingBranch(branch);
        setFormData({
            name: branch.name || '',
            code: branch.code || '',
            address: branch.address || '',
            district: branch.district || '',
            province: branch.province || '',
            phone: Array.isArray(branch.phone) ? branch.phone : [branch.phone || ''],
            fax: branch.fax || '',
            email: branch.email || '',
            hours: branch.hours || 'Mon - Fri : 8:30 AM - 5:00 PM | Sat : 8:30 AM - 1:00 PM',
            services: branch.services || [],
            coordinates: {
                lat: branch.coordinates?.lat || '',
                lng: branch.coordinates?.lng || ''
            },
            isHeadOffice: branch.isHeadOffice || false
        });
        setShowModal(true);
    };

    // Handle save
    const handleSave = async () => {
        // Validation
        if (!formData.name.trim()) {
            showNotification('Branch name is required', 'error');
            return;
        }
        if (!formData.address.trim()) {
            showNotification('Address is required', 'error');
            return;
        }

        setSaving(true);
        try {
            const branchData = {
                ...formData,
                phone: formData.phone.filter(p => p.trim()),
                coordinates: {
                    lat: parseFloat(formData.coordinates.lat) || 0,
                    lng: parseFloat(formData.coordinates.lng) || 0
                }
            };

            if (editingBranch?.id) {
                // Update existing branch
                const response = await branchesAPI.update(editingBranch.id, branchData);
                if (response.ok) {
                    showNotification('Branch updated successfully!');
                } else {
                    throw new Error('Failed to update branch');
                }
            } else {
                // Create new branch
                const response = await branchesAPI.create(branchData);
                if (response.ok) {
                    showNotification('Branch created successfully!');
                } else {
                    throw new Error('Failed to create branch');
                }
            }

            setShowModal(false);
            resetForm();
        } catch (error) {
            console.error('Error saving branch:', error);
            showNotification(error.message || 'Failed to save branch', 'error');
        } finally {
            setSaving(false);
        }
    };

    // Handle delete
    const handleDelete = async (id) => {
        try {
            const response = await branchesAPI.delete(id);
            if (response.ok) {
                showNotification('Branch deleted successfully!');
            } else {
                throw new Error('Failed to delete branch');
            }
        } catch (error) {
            console.error('Error deleting branch:', error);
            showNotification(error.message || 'Failed to delete branch', 'error');
        }
        setDeleteConfirm(null);
    };

    // Add phone field
    const addPhoneField = () => {
        setFormData(prev => ({
            ...prev,
            phone: [...prev.phone, '']
        }));
    };

    // Remove phone field
    const removePhoneField = (index) => {
        setFormData(prev => ({
            ...prev,
            phone: prev.phone.filter((_, i) => i !== index)
        }));
    };

    // Update phone field
    const updatePhoneField = (index, value) => {
        setFormData(prev => ({
            ...prev,
            phone: prev.phone.map((p, i) => i === index ? value : p)
        }));
    };

    // Toggle service
    const toggleService = (service) => {
        setFormData(prev => ({
            ...prev,
            services: prev.services.includes(service)
                ? prev.services.filter(s => s !== service)
                : [...prev.services, service]
        }));
    };

    // Get current location
    const getCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setFormData(prev => ({
                        ...prev,
                        coordinates: {
                            lat: position.coords.latitude.toFixed(6),
                            lng: position.coords.longitude.toFixed(6)
                        }
                    }));
                    showNotification('Location captured successfully!');
                },
                (error) => {
                    showNotification('Unable to get location: ' + error.message, 'error');
                }
            );
        } else {
            showNotification('Geolocation is not supported by this browser', 'error');
        }
    };

    return (
        <AdminLayout title="Branches">
            <Helmet>
                <title>Manage Branches - Admin Dashboard</title>
            </Helmet>

            {/* Notification */}
            {notification && (
                <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg animate-slide-in ${notification.type === 'error' ? 'bg-red-500' : 'bg-green-500'
                    } text-white`}>
                    {notification.type === 'error' ? (
                        <AlertCircle className="w-5 h-5" />
                    ) : (
                        <CheckCircle className="w-5 h-5" />
                    )}
                    <span className="font-medium">{notification.message}</span>
                </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg shadow-teal-500/25">
                            <Building2 className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">{branches.length}</p>
                            <p className="text-sm text-gray-400">Total Branches</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/25">
                            <Star className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">
                                {branches.filter(b => b.isHeadOffice).length}
                            </p>
                            <p className="text-sm text-gray-400">Head Offices</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                            <MapPin className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">
                                {new Set(branches.map(b => b.province)).size}
                            </p>
                            <p className="text-sm text-gray-400">Provinces Covered</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Action Bar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div className="flex items-center gap-3">
                    <p className="text-gray-400 text-sm">
                        {filteredBranches.length} of {branches.length} branches
                    </p>
                </div>
                <button
                    onClick={handleAdd}
                    className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-5 py-2.5 rounded-xl font-semibold hover:from-teal-400 hover:to-emerald-400 transition-all shadow-lg shadow-teal-500/25 flex items-center gap-2"
                >
                    <Plus className="w-5 h-5" />
                    Add Branch
                </button>
            </div>

            {/* Table Card */}
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
                {/* Toolbar */}
                <div className="p-4 border-b border-white/10 flex gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search branches..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50"
                        />
                    </div>
                </div>

                {/* List */}
                {loading ? (
                    <div className="p-8 text-center text-gray-400">
                        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                        Loading branches...
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-white/5 text-gray-400 font-medium text-sm">
                                <tr>
                                    <th className="px-6 py-4">Branch</th>
                                    <th className="px-6 py-4">Location</th>
                                    <th className="px-6 py-4">Contact</th>
                                    <th className="px-6 py-4">Services</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {filteredBranches.length > 0 ? (
                                    filteredBranches.map((branch) => (
                                        <tr key={branch.id} className="hover:bg-white/5 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg flex items-center justify-center text-white font-bold">
                                                        {branch.name?.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold text-white flex items-center gap-2">
                                                            {branch.name}
                                                            {branch.isHeadOffice && (
                                                                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 text-xs rounded-full font-medium border border-amber-500/30">
                                                                    HQ
                                                                </span>
                                                            )}
                                                        </p>
                                                        <p className="text-sm text-gray-500">Code: {branch.code}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="text-sm text-white">{branch.district}</p>
                                                <p className="text-sm text-gray-500">{branch.province}</p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="text-sm text-white">
                                                    {Array.isArray(branch.phone) ? branch.phone[0] : branch.phone}
                                                </p>
                                                <p className="text-sm text-gray-500">{branch.email}</p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-wrap gap-1">
                                                    {branch.services?.slice(0, 2).map((service, idx) => (
                                                        <span
                                                            key={idx}
                                                            className="px-2 py-0.5 bg-teal-500/20 text-teal-300 text-xs rounded-full border border-teal-500/30"
                                                        >
                                                            {service}
                                                        </span>
                                                    ))}
                                                    {branch.services?.length > 2 && (
                                                        <span className="px-2 py-0.5 bg-white/10 text-gray-400 text-xs rounded-full">
                                                            +{branch.services.length - 2}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex justify-end gap-1">
                                                    {branch.coordinates?.lat && branch.coordinates?.lng && (
                                                        <a
                                                            href={`https://www.google.com/maps?q=${branch.coordinates.lat},${branch.coordinates.lng}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                                                            title="View on Map"
                                                        >
                                                            <Map className="w-4 h-4" />
                                                        </a>
                                                    )}
                                                    <button
                                                        onClick={() => handleEdit(branch)}
                                                        className="p-2 text-gray-400 hover:text-teal-400 hover:bg-teal-500/10 rounded-lg transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteConfirm(branch)}
                                                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-12 text-center">
                                            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                                                <Building2 className="w-8 h-8 text-gray-500" />
                                            </div>
                                            <p className="text-gray-400 mb-2">No branches found</p>
                                            <button
                                                onClick={handleAdd}
                                                className="text-teal-400 hover:underline text-sm"
                                            >
                                                Add your first branch →
                                            </button>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                            <h2 className="text-xl font-bold text-gray-900">
                                {editingBranch ? 'Edit Branch' : 'Add New Branch'}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowModal(false);
                                    resetForm();
                                }}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-6">
                            {/* Basic Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Branch Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                        placeholder="e.g., Colombo Main Branch"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Branch Code
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.code}
                                        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                        placeholder="e.g., 001"
                                    />
                                </div>
                            </div>

                            {/* Address */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Address *
                                </label>
                                <textarea
                                    value={formData.address}
                                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                    rows={2}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                    placeholder="Full address"
                                />
                            </div>

                            {/* District & Province */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        District
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.district}
                                        onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                        placeholder="e.g., Colombo"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Province
                                    </label>
                                    <select
                                        value={formData.province}
                                        onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                    >
                                        <option value="">Select Province</option>
                                        {provinces.map((p) => (
                                            <option key={p} value={p}>{p}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Phone Numbers */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Phone Numbers
                                </label>
                                {formData.phone.map((phone, index) => (
                                    <div key={index} className="flex gap-2 mb-2">
                                        <input
                                            type="text"
                                            value={phone}
                                            onChange={(e) => updatePhoneField(index, e.target.value)}
                                            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                            placeholder="+94 11 234 5678"
                                        />
                                        {formData.phone.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removePhoneField(index)}
                                                className="p-2.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                            >
                                                <X className="w-5 h-5" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={addPhoneField}
                                    className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                                >
                                    + Add another phone
                                </button>
                            </div>

                            {/* Email & Fax */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                        placeholder="branch@company.lk"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Fax
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.fax}
                                        onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                        placeholder="+94 11 234 5680"
                                    />
                                </div>
                            </div>

                            {/* Working Hours */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Working Hours
                                </label>
                                <input
                                    type="text"
                                    value={formData.hours}
                                    onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                    placeholder="Mon - Fri : 8:30 AM - 5:00 PM"
                                />
                            </div>

                            {/* Coordinates */}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Location Coordinates (for Get Directions)
                                    </label>
                                    <button
                                        type="button"
                                        onClick={getCurrentLocation}
                                        className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
                                    >
                                        <Navigation className="w-4 h-4" />
                                        Get Current Location
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <input
                                        type="text"
                                        value={formData.coordinates.lat}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            coordinates: { ...formData.coordinates, lat: e.target.value }
                                        })}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                        placeholder="Latitude (e.g., 6.9271)"
                                    />
                                    <input
                                        type="text"
                                        value={formData.coordinates.lng}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            coordinates: { ...formData.coordinates, lng: e.target.value }
                                        })}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                        placeholder="Longitude (e.g., 79.8612)"
                                    />
                                </div>
                                {formData.coordinates.lat && formData.coordinates.lng && (
                                    <a
                                        href={`https://www.google.com/maps?q=${formData.coordinates.lat},${formData.coordinates.lng}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mt-2"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                        Preview on Google Maps
                                    </a>
                                )}
                            </div>

                            {/* Services */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Services Available
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {availableServices.map((service) => (
                                        <button
                                            key={service}
                                            type="button"
                                            onClick={() => toggleService(service)}
                                            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${formData.services.includes(service)
                                                ? 'bg-teal-600 text-white'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }`}
                                        >
                                            {service}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Head Office Toggle */}
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="isHeadOffice"
                                    checked={formData.isHeadOffice}
                                    onChange={(e) => setFormData({ ...formData, isHeadOffice: e.target.checked })}
                                    className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                                />
                                <label htmlFor="isHeadOffice" className="text-sm font-medium text-gray-700">
                                    This is the Head Office
                                </label>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowModal(false);
                                    resetForm();
                                }}
                                className="px-4 py-2.5 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="flex items-center gap-2 px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-5 h-5" />
                                        Save Branch
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
                        <div className="text-center">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Trash2 className="w-6 h-6 text-red-600" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Branch</h3>
                            <p className="text-gray-500 mb-6">
                                Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setDeleteConfirm(null)}
                                    className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleDelete(deleteConfirm.id)}
                                    className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes slide-in {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                .animate-slide-in {
                    animation: slide-in 0.3s ease-out;
                }
            `}</style>
        </AdminLayout>
    );
};

export default BranchesManagementPage;
